import React, { useState, useEffect } from "react";
import { Card } from "views/components/ui/Card";
import {
    Loader2,
    Save,
    FileText,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchBlogPosts,
    fetchBlogPost,
    updateBlogPost,
    clearSelectedPost,
    clearError,
} from "actions/admin";

const POSTS_PER_PAGE = 20;

const BlogPostEditor = () => {
    const dispatch = useDispatch();
    const { posts, selectedPost, isLoading, error } = useSelector(
        (state) => state.admin
    );
    const [content, setContent] = useState("");
    const [originalContent, setOriginalContent] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    // Calculate if content has been modified
    const hasContentChanged = content !== originalContent;

    useEffect(() => {
        dispatch(fetchBlogPosts());
        return () => {
            dispatch(clearSelectedPost());
            dispatch(clearError());
        };
    }, [dispatch]);

    // Calculate pagination values
    const totalPages = Math.ceil((posts?.length || 0) / POSTS_PER_PAGE);
    const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
    const endIndex = startIndex + POSTS_PER_PAGE;
    const currentPosts = posts?.slice(startIndex, endIndex) || [];

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handlePostSelect = async (post) => {
        // If there are unsaved changes, confirm before switching posts
        if (hasContentChanged) {
            if (
                !window.confirm(
                    "You have unsaved changes. Are you sure you want to switch posts?"
                )
            ) {
                return;
            }
        }
        dispatch(fetchBlogPost(post.filename));
    };

    const handleSave = async () => {
        if (!selectedPost || !hasContentChanged) return;

        try {
            setIsSaving(true);
            await dispatch(
                updateBlogPost({
                    filename: selectedPost.filename,
                    content,
                })
            ).unwrap();
            // Update original content after successful save
            setOriginalContent(content);
        } catch (err) {
            // Error is handled by the reducer
        } finally {
            setIsSaving(false);
        }
    };

    // Update content when selectedPost changes
    useEffect(() => {
        if (selectedPost?.content) {
            setContent(selectedPost.content);
            setOriginalContent(selectedPost.content);
        } else {
            setContent("");
            setOriginalContent("");
        }
    }, [selectedPost]);

    if (isLoading && !selectedPost) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Posts List */}
            <Card className="lg:col-span-1">
                <div className="p-4">
                    <h2 className="text-lg font-semibold mb-4">Blog Posts</h2>
                    <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                        {currentPosts.map((post) => (
                            <button
                                key={post.filename}
                                onClick={() => handlePostSelect(post)}
                                className={`w-full text-left px-4 py-2 rounded-md flex items-center gap-2 ${
                                    selectedPost?.filename === post.filename
                                        ? "bg-blue-50 text-blue-700"
                                        : "hover:bg-gray-50"
                                }`}
                            >
                                <FileText className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate">{post.title}</span>
                            </button>
                        ))}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="mt-4 flex items-center justify-between border-t pt-4">
                            <button
                                onClick={() =>
                                    handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                <ChevronLeft className="h-4 w-4" />
                                Previous
                            </button>
                            <span className="text-sm text-gray-600">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                onClick={() =>
                                    handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                Next
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                </div>
            </Card>

            {/* Editor */}
            <Card className="lg:col-span-2">
                <div className="p-4">
                    {selectedPost ? (
                        <>
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold">
                                    {selectedPost.title}
                                </h2>
                                <button
                                    onClick={handleSave}
                                    disabled={
                                        isSaving ||
                                        isLoading ||
                                        !hasContentChanged
                                    }
                                    className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                                        hasContentChanged
                                            ? "bg-blue-600 text-white hover:bg-blue-700"
                                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    }`}
                                >
                                    {isSaving ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Save className="h-4 w-4" />
                                    )}
                                    Save
                                </button>
                            </div>
                            <div className="space-y-4">
                                <div className="border rounded-md">
                                    <textarea
                                        value={content}
                                        onChange={(e) =>
                                            setContent(e.target.value)
                                        }
                                        className="w-full h-[600px] p-4 font-mono text-sm resize-none focus:outline-none"
                                        placeholder="Write your markdown content here..."
                                    />
                                </div>
                                {error && (
                                    <div className="text-red-600 text-sm">
                                        {error}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            Select a post to edit
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default BlogPostEditor;
